import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import {
  BENCH_ATTACHMENT_CONTENT_TYPES,
  BENCH_ATTACHMENT_MAX_BYTES,
  BENCH_NOTES_MAX_CHARS,
} from '@siliconx/shared/bench';
import {
  deleteBenchRunAttachment,
  getBenchAttachmentDownloadUrl,
  getBenchRunAnnotations,
  saveBenchRunNotes,
  uploadBenchRunAttachment,
} from '../../lib/benchAnnotations';
import { formatDate } from '../../lib/format';
import { qk } from '../../lib/queryKeys';

const NOT_VERIFIED = 'Customer-supplied, not verified. Not part of the signed bench record.';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Notes and attachments of one bench run (SIL-2333). Text is rendered as a text node with
 * `whitespace-pre-wrap`: no markdown, no HTML. Editing is offered to the run's own user and org admins.
 */
export function RunNotesAttachments({ runId }: { runId: string }) {
  const queryClient = useQueryClient();
  const key = qk.bench.runAnnotations(runId);
  const query = useQuery({ queryKey: key, queryFn: () => getBenchRunAnnotations(runId) });
  const data = query.data;
  const canEdit = data?.canEdit === true;

  const [draft, setDraft] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(null), [data?.notes]);

  const save = useMutation({
    mutationFn: (notes: string) => saveBenchRunNotes(runId, notes),
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: () => toast.error('The note was not saved. Try again.'),
  });

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    if (!(file.type in BENCH_ATTACHMENT_CONTENT_TYPES)) {
      toast.error('Attachment must be a PDF, JPEG, or PNG file');
      return;
    }
    if (file.size > BENCH_ATTACHMENT_MAX_BYTES) {
      toast.error('Attachment must be 20 MB or smaller');
      return;
    }
    setBusy(true);
    try {
      await uploadBenchRunAttachment(runId, file);
      toast.success('Attachment uploaded');
      void queryClient.invalidateQueries({ queryKey: key });
    } catch (ex: unknown) {
      toast.error((ex as Error).message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  async function onDownload(attachmentId: string) {
    try {
      window.open(await getBenchAttachmentDownloadUrl(runId, attachmentId), '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Could not fetch the download link');
    }
  }

  async function onDelete(attachmentId: string, fileName: string) {
    if (!confirm(`Delete attachment "${fileName}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await deleteBenchRunAttachment(runId, attachmentId);
      toast.success('Attachment deleted');
      void queryClient.invalidateQueries({ queryKey: key });
    } catch {
      toast.error('Delete failed');
    } finally {
      setBusy(false);
    }
  }

  if (query.isError) return null;
  if (!data) return null;

  const notesValue = draft ?? data.notes ?? '';
  const dirty = draft !== null && draft !== (data.notes ?? '');

  return (
    <>
      <Card className="space-y-3" data-testid="run-notes">
        <PanelTitle title="Notes" />
        <p className="text-xs text-muted-foreground">{NOT_VERIFIED}</p>
        {canEdit ? (
          <>
            <textarea
              aria-label="Run notes"
              className="min-h-24 w-full rounded border border-border bg-background p-2 text-sm"
              maxLength={BENCH_NOTES_MAX_CHARS}
              value={notesValue}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {notesValue.length} / {BENCH_NOTES_MAX_CHARS}
              </span>
              <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate(notesValue)}>
                {save.isPending ? 'Saving…' : 'Save note'}
              </Button>
            </div>
          </>
        ) : data.notes ? (
          <p className="whitespace-pre-wrap text-sm">{data.notes}</p>
        ) : (
          <p className="text-sm text-muted-foreground">No notes.</p>
        )}
      </Card>

      <Card className="space-y-3" data-testid="run-attachments">
        <div className="flex items-center justify-between">
          <PanelTitle title="Attachments" />
          {canEdit && (
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                className="hidden"
                onChange={onFile}
              />
              <Button size="sm" disabled={busy} onClick={() => fileRef.current?.click()}>
                {busy ? 'Working…' : 'Upload'}
              </Button>
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{NOT_VERIFIED} PDF, JPEG, or PNG up to 20 MB.</p>
        {data.attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No attachments yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {data.attachments.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-4 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{a.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatBytes(a.sizeBytes)} · {formatDate(a.createdAt)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <button type="button" onClick={() => void onDownload(a.id)} className="text-sm text-blue-600 hover:text-blue-800">
                    Download
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void onDelete(a.id, a.fileName)}
                      className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
